import { useEffect, useMemo, useRef } from 'react'
import {
  CurecallDatePicker,
  CurecallDatePickerHandle,
  CurecallMultiSelect,
  CurecallMultiSelectItem,
  ECurecallSize,
  MedicalProfessionalMonochromeIcon,
} from '@honestica/curecall-ui'
import { TPlanningFilters } from '../planning.type'
import { useTranslation } from 'react-i18next'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { getDoctorCivilityAndName, getDoctorName } from 'src/utils/string-formatter.util'
import { TMedicalStaffMember } from 'src/types/medical-staff-member'
import dayjs from 'dayjs'

interface Props {
  currentFilters: TPlanningFilters
  updatePlanningFilters: (filters: Partial<TPlanningFilters>) => void
  resetFilters: () => void
}

export default function PlanningFilters({
  currentFilters,
  updatePlanningFilters,
  resetFilters,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const { activeDoctors } = useCurrentProject()

  const noMedicalStaffMemberOption = {
    label: t('pages.planning.filters.not-specified'),
    value: null,
    isPinned: true,
  }
  const medicalStaffOptions = useMemo(
    () => [
      noMedicalStaffMemberOption,
      ...activeDoctors
        .slice() // do not mutate original array
        .sort((doc1: TMedicalStaffMember, doc2: TMedicalStaffMember) => {
          const lastNameCompare = doc1.lastname?.localeCompare(doc2.lastname, 'fr', { sensitivity: 'base' })
          if (lastNameCompare !== 0) return lastNameCompare
          return doc1.firstname?.localeCompare(doc2.firstname, 'fr', { sensitivity: 'base' })
        })
        .map((doctor) => ({
          label: getDoctorCivilityAndName(doctor),
          labelToSortBy: getDoctorName(doctor),
          value: doctor.id,
        })),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeDoctors],
  )
  const selectedMedicalStaffMembers = useMemo<CurecallMultiSelectItem[]>(
    () =>
      medicalStaffOptions.filter((opt) =>
        currentFilters.medicalStaffMemberIds?.includes(opt.value as number),
      ),
    [currentFilters.medicalStaffMemberIds, medicalStaffOptions],
  )
  const datePickerRef = useRef<CurecallDatePickerHandle>(null)

  const _updatePlanningFilters = (date: string | null) => {
    const selectedDate = dayjs(date).startOf('day')
    const today = dayjs().startOf('day')
    const diffInDays = selectedDate.diff(today, 'day')
    updatePlanningFilters({ relativeDaysFromToday: diffInDays })
  }

  const onChangeDateFilter = (date: string | null) => {
    const currentRelativeDaysFromToday = dayjs(date).startOf('day').diff(dayjs().startOf('day'), 'day')
    const hasDateChanged = currentFilters.relativeDaysFromToday !== currentRelativeDaysFromToday
    if (date && hasDateChanged) {
      _updatePlanningFilters(date)
    }
  }

  const onChangeMedicalStaffFilter = (medicalStaffMembers: CurecallMultiSelectItem[]) => {
    updatePlanningFilters({
      medicalStaffMemberIds: medicalStaffMembers.map((opt) => opt.value),
    })
  }

  useEffect(() => {
    // if currentTab is changed, or user update url
    const relativeDays = currentFilters.relativeDaysFromToday
    const newDate = dayjs().add(relativeDays, 'day').toDate()
    datePickerRef.current?.setToDate(newDate)
  }, [currentFilters.relativeDaysFromToday])

  return (
    <div className="c-flex c-items-end c-gap-2" data-planning-tour="filters">
      <div className="c-w-[210px]">
        <CurecallDatePicker
          ref={datePickerRef}
          ariaLabel={t('pages.planning.filters.med-event-date')}
          labelClassName="tw-p-0 tw-m-0"
          onChangeDate={onChangeDateFilter}
          displayFullDate={true}
          size={ECurecallSize.SMALL}
          iconPosition="left"
        />
      </div>
      <div className="c-w-[270px] c-relative c-z-20">
        <CurecallMultiSelect
          aria-label={t('pages.planning.filters.medical-staff-member')}
          labelClassName="tw-p-0 tw-m-0"
          inputClassName="tw-h-8 [&_li]:c-h-6 [&_li]:c-p-1 !c-pl-10"
          optionsClassName="tw-max-h-[300px] tw-h-[300px]"
          items={medicalStaffOptions}
          selectedItems={selectedMedicalStaffMembers}
          selectedElementsLabel={t('pages.planning.filters.doctor-selected', {
            number: selectedMedicalStaffMembers.length,
          })}
          IconLeft={<MedicalProfessionalMonochromeIcon className="tw-text-primary" />}
          isSearchable={true}
          searchPlaceholder={t('pages.planning.filters.medical-staff-member-search-placeholder')}
          noResultText={t('pages.planning.filters.medical-staff-member-no-result')}
          placeholder={t('pages.planning.filters.medical-staff-member-placeholder')}
          openOptionsAriaLabel={t('pages.planning.filters.medical-staff-member-open-options')}
          closeOptionsAriaLabel={t('pages.planning.filters.medical-staff-member-close-options')}
          onSelect={onChangeMedicalStaffFilter}
        />
      </div>
      <button
        className="c-text-primary c-font-bold hover:c-underline c-ml-1 c-mb-2"
        onClick={resetFilters}
        type="button"
      >
        {t('pages.planning.filters.reset-filters')}
      </button>
    </div>
  )
}
